/**
 * Local guest session — onboarding + subscription without account login.
 * Persists to localStorage and mirrors guest id in a cookie for resilience.
 */

const GUEST_ID_KEY = 'vfootball-guest-id';
const ONBOARDING_KEY = 'vfootball-onboarding-complete';
const SUBSCRIPTION_KEY = 'vfootball-subscription';
const GUEST_COOKIE = 'lf_guest';
const ONBOARDING_COOKIE = 'lf_onboarding';
const COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

function readCookie(name) {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function writeCookie(name, value, maxAgeSec = COOKIE_MAX_AGE_SEC) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure}`;
}

function clearCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function newGuestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `guest_${crypto.randomUUID()}`;
  }
  return `guest_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function getOrCreateGuestId() {
  let id = localStorage.getItem(GUEST_ID_KEY) || readCookie(GUEST_COOKIE);
  if (!id) {
    id = newGuestId();
    localStorage.setItem(GUEST_ID_KEY, id);
    writeCookie(GUEST_COOKIE, id);
    return id;
  }
  localStorage.setItem(GUEST_ID_KEY, id);
  if (readCookie(GUEST_COOKIE) !== id) {
    writeCookie(GUEST_COOKIE, id);
  }
  return id;
}

export function readOnboardingComplete() {
  try {
    if (localStorage.getItem(ONBOARDING_KEY) === '1') return true;
  } catch {
    /* private mode / storage blocked */
  }

  if (readCookie(ONBOARDING_COOKIE) === '1') {
    try {
      localStorage.setItem(ONBOARDING_KEY, '1');
    } catch {
      /* cookie is enough */
    }
    return true;
  }

  return false;
}

export function writeOnboardingComplete() {
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* fall back to cookie only */
  }
  writeCookie(ONBOARDING_COOKIE, '1');
}

export function readStoredSubscription() {
  try {
    const raw = localStorage.getItem(SUBSCRIPTION_KEY);
    if (!raw) return null;
    const sub = JSON.parse(raw);
    if (!sub?.expiresAt) return null;
    return sub;
  } catch {
    return null;
  }
}

export function writeStoredSubscription(payload) {
  if (!payload) {
    localStorage.removeItem(SUBSCRIPTION_KEY);
    return null;
  }
  localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify(payload));
  return payload;
}

export function subscriptionIsActive(sub = readStoredSubscription()) {
  return Boolean(sub?.expiresAt && Number(sub.expiresAt) > Date.now());
}

export function clearGuestSession({ keepGuestId = true } = {}) {
  try {
    localStorage.removeItem(SUBSCRIPTION_KEY);
    localStorage.removeItem(ONBOARDING_KEY);
    if (!keepGuestId) {
      localStorage.removeItem(GUEST_ID_KEY);
    }
  } catch {
    /* ignore */
  }
  clearCookie(ONBOARDING_COOKIE);
  if (!keepGuestId) {
    clearCookie(GUEST_COOKIE);
  }
}

export { GUEST_ID_KEY, ONBOARDING_KEY, SUBSCRIPTION_KEY };
